import type { ActionFunction } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type {
    OptOutOfSaleOrSharingMutation,
    OptOutOfSaleOrSharingMutationVariables,
} from 'generated/graphql';
import { OptOutOfSaleOrSharingDocument } from 'generated/graphql';
import { buildSaleSharingOptOutCookie } from '@modules/privacy/sale-sharing-opt-out-cookie';
import { saleSharingOptOutSchema } from '@modules/privacy/sale-sharing-opt-out-schema';

/**
 * Endpoint for the "Do not sell or share my personal information" form on
 * `/your-privacy-choices`. Import this constant in the client instead of
 * hardcoding the path.
 */
export const SALE_SHARING_OPT_OUT_API_ROUTE =
    '/remix-api/privacy/sale-sharing-opt-out';

/**
 * Records an opt-out of the sale/sharing of personal information and sets the
 * browser opt-out cookie.
 *
 * The two halves are independent on purpose. The cookie is the browser-level
 * opt-out and takes effect immediately for whoever is sitting at this browser,
 * whether or not the email matches an account — so it is set even when the
 * backend call fails, because a person who submitted the form has exercised
 * their choice and a transient API error must not silently drop it. The
 * mutation is what carries the choice beyond this browser, to the matched
 * member and to the DSR runbook.
 *
 * A failed mutation still returns 500 so the form can ask the person to try
 * again; the cookie riding along on that response means their browser is
 * already covered while they do. That cookie carries a different value in the
 * two cases, so a browser-only opt-out is never mistaken for proof the record
 * was written and the person keeps being asked to finish.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const formData = await request.formData();
    const parsed = saleSharingOptOutSchema.safeParse(
        Object.fromEntries(formData),
    );

    if (!parsed.success) {
        return routerData(
            { ok: false, errors: parsed.error.flatten().fieldErrors },
            { status: 400 },
        );
    }

    const cookieFor = (recorded: boolean) => ({
        'Set-Cookie': buildSaleSharingOptOutCookie(
            context.config.cookies.topDomain,
            recorded,
        ),
    });

    try {
        const { data } = await context.client.mutate<
            OptOutOfSaleOrSharingMutation,
            OptOutOfSaleOrSharingMutationVariables
        >({
            mutation: OptOutOfSaleOrSharingDocument,
            variables: { Input: { Email: parsed.data.email } },
        });

        const recorded = data?.optOutOfSaleOrSharing?.Success ?? false;

        return routerData(
            { ok: recorded },
            { headers: cookieFor(recorded), status: recorded ? 200 : 500 },
        );
    } catch (err) {
        // No email address in the log line: this is an unauthenticated endpoint and
        // the address is the personal information the request is about.
        context.logger.error(
            '[sale-sharing-opt-out] failed to record opt-out',
            err,
        );
        return routerData(
            { ok: false },
            { status: 500, headers: cookieFor(false) },
        );
    }
};
